import { Link, createFileRoute } from "@tanstack/react-router";
import { CalendarClock, Compass, Layers, Orbit, Sparkles, Star } from "lucide-react";

export const Route = createFileRoute("/bat-tu-chi-tiet")({
  head: () => ({
    meta: [
      { title: "Giải Thích Bát Tự, Tứ Trụ - Can Chi, Thập Thần, Ngũ Hành" },
      {
        name: "description",
        content:
          "Tìm hiểu Bát Tự / Tứ Trụ: bốn trụ Năm–Tháng–Ngày–Giờ, Thiên Can Địa Chi, Nhật chủ, Thập thần, ngũ hành và đại vận. Tìm hiểu ngay!",
      },
      { property: "og:title", content: "Giải Thích Bát Tự, Tứ Trụ - Can Chi, Thập Thần, Ngũ Hành" },
      {
        property: "og:description",
        content:
          "Nhập môn Bát Tự / Tứ Trụ: Tứ Trụ, Nhật chủ vượng nhược, Thập thần, dụng thần và đại vận.",
      },
      { property: "og:url", content: "https://boide.online/bat-tu-chi-tiet" },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/bat-tu-chi-tiet" }],
  }),
  component: BatTuDetailPage,
});

const foundations = [
  {
    title: "Tứ Trụ",
    icon: Compass,
    body: "Bốn cặp Can – Chi của Năm, Tháng, Ngày, Giờ sinh. Mỗi trụ ứng với một giai đoạn và một nhóm mối quan hệ trong cuộc đời đương số.",
  },
  {
    title: "Nhật chủ",
    icon: Star,
    body: "Thiên Can của trụ Ngày chính là bản thân đương số. Xét Nhật chủ vượng hay nhược theo mùa sinh và cán cân ngũ hành là bước nền của mọi luận giải.",
  },
  {
    title: "Thập thần",
    icon: Layers,
    body: "Quan hệ sinh – khắc giữa các can với Nhật chủ tạo ra mười thần: Tỷ Kiên, Kiếp Tài, Thực Thần, Thương Quan, Chính/Thiên Tài, Chính Quan, Thất Sát, Chính/Thiên Ấn — luận tài, quan, ấn, lục thân.",
  },
  {
    title: "Đại vận & dụng thần",
    icon: CalendarClock,
    body: "Đại vận đi từng bước 10 năm theo chiều thuận/nghịch. Dụng thần là hành cần bổ để cân bằng lá số, gợi ý hướng cải vận về nghề, màu sắc, phương vị.",
  },
];

const pillars = ["Trụ Năm", "Trụ Tháng", "Trụ Ngày", "Trụ Giờ"];

const elements = ["Mộc", "Hỏa", "Thổ", "Kim", "Thủy"];

const readingLayers = [
  "Xác định Nhật chủ và mùa sinh (lệnh tháng) để biết Nhật chủ vượng hay nhược.",
  "Cân ngũ hành cả lá số, tìm hành thiếu/thừa để chọn dụng thần và kỵ thần.",
  "Đọc Thập thần trên bốn trụ để luận tính cách, sự nghiệp, tài lộc, tình duyên và lục thân.",
  "Xét quan hệ địa chi: tam hợp, lục hợp, lục xung, lục hại để thấy trợ lực và xung đột.",
  "Đối chiếu đại vận và lưu niên để biết thời điểm một tiềm năng được kích hoạt mạnh hay yếu.",
];

function BatTuDetailPage() {
  return (
    <main className="flex-1 overflow-y-auto px-4 py-8 pb-24 sm:px-6 lg:px-12 lg:pb-12">
      <div className="mx-auto max-w-6xl space-y-8">
        <section className="rounded-3xl border border-gold-soft bg-card/50 p-6 shadow-mystic backdrop-blur sm:p-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold-soft/60 bg-background/40 px-3 py-1 text-xs uppercase tracking-[0.25em] text-primary">
              <Orbit className="h-3.5 w-3.5" /> Nhập môn
            </span>
            <h1 className="mt-5 text-3xl font-semibold text-gold-gradient lg:text-5xl">
              Bát Tự đọc vận mệnh qua bốn trụ Can Chi.
            </h1>
            <p className="mt-5 text-base leading-8 text-foreground/85 lg:text-lg">
              Bát Tự (Tứ Trụ) lấy năm, tháng, ngày, giờ sinh chuyển thành tám chữ Thiên Can – Địa
              Chi. Từ tám chữ này, ta soi Nhật chủ, cán cân ngũ hành, Thập thần và đại vận để hiểu
              khuynh hướng, điểm mạnh, thử thách và chu kỳ vận động của từng giai đoạn.
            </p>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          {foundations.map((item) => (
            <article
              key={item.title}
              className="rounded-2xl border border-gold-soft/60 bg-background/30 p-5 backdrop-blur"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <item.icon className="h-4 w-4" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{item.title}</h3>
              </div>
              <p className="mt-3 text-sm leading-7 text-muted-foreground">{item.body}</p>
            </article>
          ))}
        </section>

        <section className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <article className="rounded-2xl border border-gold-soft bg-card/40 p-6 backdrop-blur">
            <span className="text-xs uppercase tracking-[0.25em] text-primary">Bốn trụ</span>
            <div className="mt-5 grid grid-cols-2 gap-2">
              {pillars.map((pillar) => (
                <div
                  key={pillar}
                  className="rounded-xl border border-primary/15 bg-background/25 px-3 py-2 text-sm text-foreground/90"
                >
                  {pillar}
                </div>
              ))}
            </div>
            <span className="mt-6 block text-xs uppercase tracking-[0.25em] text-primary">
              Ngũ hành
            </span>
            <div className="mt-3 flex flex-wrap gap-2">
              {elements.map((el) => (
                <span
                  key={el}
                  className="rounded-full border border-primary/15 bg-background/25 px-3 py-1 text-sm text-foreground/90"
                >
                  {el}
                </span>
              ))}
            </div>
            <p className="mt-5 text-sm leading-7 text-muted-foreground">
              Lá số cân bằng ngũ hành thì hanh thông; thiếu hoặc thừa một hành sẽ gợi ý dụng thần để
              bổ khuyết và cải vận.
            </p>
          </article>

          <article className="rounded-2xl border border-gold-soft bg-card/40 p-6 backdrop-blur">
            <span className="text-xs uppercase tracking-[0.25em] text-primary">
              Cách đọc theo tầng
            </span>
            <ol className="mt-5 space-y-4">
              {readingLayers.map((step, index) => (
                <li
                  key={step}
                  className="flex gap-4 rounded-xl border border-primary/15 bg-background/25 p-4"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold-gradient text-sm font-semibold text-primary-foreground">
                    {index + 1}
                  </span>
                  <p className="text-sm leading-7 text-foreground/85">{step}</p>
                </li>
              ))}
            </ol>
          </article>
        </section>

        <section className="rounded-2xl border border-gold-soft bg-card/40 p-6 backdrop-blur">
          <span className="text-xs uppercase tracking-[0.25em] text-primary">Ví dụ luận</span>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <ExampleCard
              title="Nhật chủ vượng"
              body="Nhật chủ vượng cần hành tiết/khắc (Thực Thương, Tài, Quan Sát) làm dụng thần; người thường mạnh mẽ, chủ động nhưng cần biết tiết chế."
            />
            <ExampleCard
              title="Chính Tài"
              body="Khi hỏi tài lộc, xem Chính/Thiên Tài cùng trạng thái vượng nhược của Nhật chủ để biết khả năng giữ và sinh tài."
            />
            <ExampleCard
              title="Đại vận"
              body="Một lá số tốt nhưng chưa vào vận đẹp thì chưa ứng mạnh; khi đại vận bổ đúng dụng thần, cơ hội mới rõ rệt."
            />
          </div>
        </section>

        <section className="rounded-2xl border border-gold-soft bg-background/30 p-6 text-center backdrop-blur">
          <Sparkles className="mx-auto h-8 w-8 text-primary" />
          <h3 className="mt-3 text-2xl font-semibold text-gold-gradient">
            Muốn xem lá số của bạn?
          </h3>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-muted-foreground">
            Bát Tự cần ngày giờ sinh càng chính xác càng tốt. Hãy xem lá số như một bản đồ tham khảo
            để hiểu mình và chọn hướng đi tỉnh táo hơn.
          </p>
          <Link
            to="/bat-tu"
            className="mt-5 inline-flex rounded-full bg-gold-gradient px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-gold transition hover:scale-[1.02]"
          >
            Lập lá số Bát Tự
          </Link>
        </section>
      </div>
    </main>
  );
}

function ExampleCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-primary/15 bg-background/25 p-4">
      <h4 className="font-semibold text-primary">{title}</h4>
      <p className="mt-2 text-sm leading-7 text-muted-foreground">{body}</p>
    </div>
  );
}
